import { newIdempotencyKey, type AiOsOperationReceipt } from './ai-os'
import { ApiFailure } from './errors'

/**
 * 两个受控写口（retry / disable）的**幂等键寿命**与**409 分档**。
 *
 * # 为什么这件事要单独一个文件
 *
 * 契约把重放做成了一张只增不改的台账（`ai_os_operation_ledger`），
 * 于是同一个键的命运有五种落点，而**其中两种长得像成功、一种长得像失败**：
 *
 * | 打进来的 | 服务端回什么 | 界面该做什么 |
 * |---|---|---|
 * | 没见过的键 | 200，`replayed: false` | 正常，版本 +1 |
 * | 见过、成了、同一份请求 | 200，`replayed: true` | **别再播一次「我在改」**，版本没动 |
 * | 见过、同键不同请求 | 409 `idempotency_mismatch` | 换一把键 |
 * | 见过、上一次还在跑 | 409 `operation_in_flight` | **同一把键**，等一会儿再问 |
 * | 见过、上一次失败了 | 409 `operation_already_failed` | **换一把键**——那个键是终态 |
 *
 * 最后一行是这个文件存在的全部理由：
 *
 * > **失败过的键不许伪装成重试。** 拿它再打一次，服务端回的还是当初那份失败，
 * > 而界面要是把这当成「又试了一次」，用户会以为自己在重试——
 * > **他一次都没有真的重试过，而屏幕上什么异常都看不出来。**
 * > 真要再试一次是**另一次操作**：新的键、新的一行台账、新的审计归属。
 *
 * # 反过来那一半同样要紧：不许乱换键
 *
 * 请求根本没拿到响应（断网、反代没起来）时**必须沿用同一把键**——
 * 那正是幂等键存在的场景。换一把的话，服务端那边可能已经执行过一次，
 * 而第二把键会让它**再执行一次**。
 *
 * 所以规则写成一句：**只有「这一把键已经用掉了」时才换。**
 */

/** 一次受控写操作的结局。**每一档的下一步都不一样，所以不许合并。** */
export type AiOsOpOutcome =
  /** 成了。`replayed` 为真时**什么都没发生**，回的是当初那份结果。 */
  | { kind: 'ok'; replayed: boolean; receipt?: AiOsOperationReceipt }
  /** 版本对不上：你看的时候它已经被改过了。重取一份再来。 */
  | { kind: 'version_conflict' }
  /** 这一档不能重试（还在跑的那几档）。 */
  | { kind: 'not_retryable' }
  /** 红级还没人点。**下一步是去点那个 Action，不是再点一次重试。** */
  | { kind: 'red_needs_approval' }
  /** 已经回退掉的经验关不了。 */
  | { kind: 'not_disableable' }
  /** 同一个键配了不同载荷。 */
  | { kind: 'idempotency_mismatch' }
  /** 上一次还在跑。**不要并发再跑一遍。** */
  | { kind: 'in_flight' }
  /** 上一次失败了，而且那是终态。 */
  | { kind: 'already_failed' }
  /** 别的（网络断了、后端说了别的）。 */
  | { kind: 'other' }

/**
 * 把一次失败分档。**判据是 `code` 不是状态码**——
 * 六种 409 共用一个状态码，靠状态码分不开；而 `code` 是契约
 * （与 `app/errors.ts` 判 `step_up_required` 是同一条理由）。
 */
export function classifyFailure(error: unknown): AiOsOpOutcome {
  if (!(error instanceof ApiFailure)) return { kind: 'other' }
  switch (error.code) {
    case 'version_conflict':
      return { kind: 'version_conflict' }
    case 'not_retryable':
      return { kind: 'not_retryable' }
    case 'red_needs_approval':
      return { kind: 'red_needs_approval' }
    case 'not_disableable':
      return { kind: 'not_disableable' }
    case 'idempotency_mismatch':
      return { kind: 'idempotency_mismatch' }
    case 'operation_in_flight':
      return { kind: 'in_flight' }
    case 'operation_already_failed':
      return { kind: 'already_failed' }
    default:
      return { kind: 'other' }
  }
}

/** 一次成功（含重放）的结局。 */
export function classifySuccess(receipt: AiOsOperationReceipt | undefined): AiOsOpOutcome {
  // **`replayed` 只信服务端那个字段。** 前端拿「版本没变」去推它是另一种推断，
  // 而版本没变还有别的原因（并发下别人也没改）
  return { kind: 'ok', replayed: Boolean(receipt?.replayed), receipt }
}

/**
 * 这一把键还能不能接着用。
 *
 * **`false` = 用掉了，下一次必须换一把。**
 *
 * 只有两种情况沿用：
 *
 *   `in_flight`   问的是**同一次**操作，换一把等于另起一次并发
 *   `other`       多半是根本没拿到响应。**那正是幂等键存在的场景**——
 *                 换一把的话，服务端可能已经执行过一次，第二把会让它再执行一次
 *
 * 其余全部换新的，各有各的理由：
 *
 *   `ok`                  这次操作完成了，下一次是另一件事
 *   `already_failed`      那个键是**终态**，拿它再打只会回当初那份失败
 *   `version_conflict`    下一次要带新的 `expected_version` —— 载荷变了，
 *                         沿用同一把键就是 `idempotency_mismatch`
 *   `not_retryable`
 *   `red_needs_approval`  下一次要带上 `action_id`，同样是另一份载荷
 *   `not_disableable`
 *   `idempotency_mismatch` 这把键已经绑给别的载荷了
 */
export function keySurvives(outcome: AiOsOpOutcome): boolean {
  return outcome.kind === 'in_flight' || outcome.kind === 'other'
}

/**
 * 一次操作用的键。
 *
 * 不是一个随手 `crypto.randomUUID()`——**它有寿命**，由 {@link keySurvives} 决定。
 * 拿它去做「下一次该用哪把键」这个判断，而不是在每个调用点各写一遍。
 */
export interface OperationKey {
  readonly key: string
  /** 按这次的结局决定下一把。用掉了就换新的，没用掉就还是它。 */
  next(outcome: AiOsOpOutcome): OperationKey
}

export function mintOperationKey(key: string = newIdempotencyKey()): OperationKey {
  return {
    key,
    next(outcome) {
      return keySurvives(outcome) ? this : mintOperationKey()
    },
  }
}

/**
 * 这一次操作**真的动了东西**吗。
 *
 * 重放没有动任何东西——契约原话：「版本也没有 bump，前端据此不要再播一次
 * 『我在改』的动效」。而一次成功与一次重放在 HTTP 上都是 200，
 * 不看 `replayed` 的话，界面会对着一次什么都没发生的调用说「改好了」。
 */
export function didSomething(outcome: AiOsOpOutcome): boolean {
  return outcome.kind === 'ok' && !outcome.replayed
}


/**
 * 把一次 mutation 的结局收敛成 {@link AiOsOpOutcome}。
 *
 * 接线层用它。放在这儿而不是组件文件里，是因为**它是判据不是界面**——
 * 混在组件里会让「这次到底发生了什么」这个判断散成两处。
 */
export function outcomeOf(
  data: { operation?: AiOsOperationReceipt } | undefined,
  error: unknown,
): AiOsOpOutcome | undefined {
  if (error !== undefined && error !== null) return classifyFailure(error)
  if (data) return classifySuccess(data.operation)
  return undefined
}
